import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { bingoPatterns, createEmptyMask, getPatternLabel } from '../data/bingoPatterns';
import { sanitizeProgramSchedule } from '../utils/programTemplateUtils';
import { appendAuditEvent } from '../utils/auditTrail';
import './BingoSchedule.css';

const VALID_STATUSES = ['scheduled', 'active', 'paused', 'completed'];

const serializeCustomPattern = (mask) => mask.flat();

const deserializeCustomPattern = (flat) => {
  if (!Array.isArray(flat)) return createEmptyMask();
  if (flat.length === 5 && flat.every(Array.isArray)) {
    return flat.map((row) => row.map(Boolean));
  }
  if (flat.length !== 25) return createEmptyMask();
  return Array.from({ length: 5 }, (_, row) => flat.slice(row * 5, row * 5 + 5).map(Boolean));
};

const normalizeGame = (game, index) => ({
  ...game,
  id: game.id || `game-${index + 1}`,
  gameNumber: Number(game.gameNumber) || index + 1,
  name: game.name || `Game ${index + 1}`,
  status: VALID_STATUSES.includes(game.status)
    ? game.status
    : game.completed
      ? 'completed'
      : 'scheduled',
  generatedNumbers: Array.isArray(game.generatedNumbers) ? game.generatedNumbers : [],
  currentBall: game.currentBall || null,
  nextBall: game.nextBall?.number ? game.nextBall : null,
  customPattern: game.customPattern || null,
  startedAt: game.startedAt || null,
  completedAt: game.completedAt || null,
});

const createLegacySchedule = (eventData) => {
  const hasActiveGame = eventData.selectedPattern || eventData.prizeAmount ||
    (Array.isArray(eventData.generatedNumbers) && eventData.generatedNumbers.length > 0);

  if (!hasActiveGame) return [];

  return [{
    id: `game-${Date.now()}-1`,
    gameNumber: Number(eventData.gameNumber) || 1,
    name: `Game ${Number(eventData.gameNumber) || 1}`,
    pattern: eventData.selectedPattern || '',
    prizeAmount: eventData.prizeAmount || '',
    generatedNumbers: Array.isArray(eventData.generatedNumbers) ? eventData.generatedNumbers : [],
    currentBall: eventData.currentBall || null,
    nextBall: eventData.nextBall?.number ? eventData.nextBall : null,
    customPattern: eventData.customPattern || null,
    status: 'scheduled',
    startedAt: null,
    completedAt: null,
    completed: false,
  }];
};

function BingoSchedule() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [eventName, setEventName] = useState('');
  const [games, setGames] = useState([]);
  const [activeGameIndex, setActiveGameIndex] = useState(-1);
  const [gameName, setGameName] = useState('');
  const [pattern, setPattern] = useState('');
  const [prize, setPrize] = useState('');
  const [customPattern, setCustomPattern] = useState(createEmptyMask);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadSchedule = async () => {
      if (!eventId) {
        setError('No event was selected.');
        setIsLoading(false);
        return;
      }

      try {
        const snapshot = await getDoc(doc(db, 'bingoMachineEvents', eventId));
        if (!snapshot.exists()) throw new Error('Event not found.');

        const eventData = snapshot.data();
        const normalizedGames = (Array.isArray(eventData.gameSchedule)
          ? eventData.gameSchedule
          : createLegacySchedule(eventData)).map(normalizeGame);
        const activeIndex = normalizedGames.findIndex((game) => game.status === 'active');
        const savedGames = normalizedGames.map((game, index) =>
          game.status === 'active' && index !== activeIndex
            ? { ...game, status: 'paused' }
            : game
        );

        if (!isMounted) return;
        setEventName(eventData.eventName || eventId);
        setGames(savedGames);
        setActiveGameIndex(activeIndex);
      } catch (loadError) {
        if (isMounted) setError(loadError.message || 'Unable to load this event schedule.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadSchedule();
    return () => { isMounted = false; };
  }, [eventId]);

  const saveSchedule = async (nextGames) => {
    const previousGames = games;
    const previousActiveGameIndex = activeGameIndex;
    const nextActiveGameIndex = nextGames.findIndex((game) => game.status === 'active');
    setGames(nextGames);
    setActiveGameIndex(nextActiveGameIndex);
    setIsSaving(true);
    setError('');

    try {
      await setDoc(
        doc(db, 'bingoMachineEvents', eventId),
        { gameSchedule: nextGames, activeGameIndex: nextActiveGameIndex, lastUpdated: serverTimestamp() },
        { merge: true }
      );

      return true;
    } catch (saveError) {
      setGames(previousGames);
      setActiveGameIndex(previousActiveGameIndex);
      setError('Unable to save the schedule. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Error saving bingo schedule:', saveError);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setGameName('');
    setPattern('');
    setPrize('');
    setCustomPattern(createEmptyMask());
    setEditingIndex(null);
  };

  const toggleCustomCell = (row, column) => {
    setCustomPattern((current) => current.map((maskRow, rowIndex) =>
      rowIndex === row
        ? maskRow.map((cell, columnIndex) => columnIndex === column ? !cell : cell)
        : maskRow
    ));
  };

  const handleSubmit = async () => {
    if (!pattern || !prize || isSaving) return;

    if (editingIndex !== null) {
      const nextGames = games.map((game, index) => index === editingIndex ? {
        ...game,
        name: gameName.trim() || `Game ${index + 1}`,
        pattern,
        prizeAmount: prize,
        customPattern: pattern === 'custom'
          ? serializeCustomPattern(customPattern)
          : null,
      } : game);

      if (await saveSchedule(nextGames)) clearForm();
      return;
    }

    const nextGameNumber = games.length + 1;
    const nextGames = [...games, {
      id: `game-${Date.now()}-${nextGameNumber}`,
      gameNumber: nextGameNumber,
      name: gameName.trim() || `Game ${nextGameNumber}`,
      pattern,
      prizeAmount: prize,
      generatedNumbers: [],
      currentBall: null,
      nextBall: null,
      customPattern: pattern === 'custom'
        ? serializeCustomPattern(customPattern)
        : null,
      status: 'scheduled',
      startedAt: null,
      completedAt: null,
      completed: false,
    }];

    if (await saveSchedule(nextGames)) clearForm();
  };

  const beginEditing = (index) => {
    const game = games[index];
    if (game.status === 'active' || game.status === 'completed') return;
    setGameName(game.name || '');
    setPattern(game.pattern || '');
    setPrize(game.prizeAmount || '');
    setCustomPattern(deserializeCustomPattern(game.customPattern));
    setEditingIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const removeGame = async (indexToRemove) => {
    if (['active', 'completed'].includes(games[indexToRemove]?.status) || isSaving) return;
    const nextGames = games
      .filter((_, index) => index !== indexToRemove)
      .map((game, index) => ({ ...game, gameNumber: index + 1 }));

    if (editingIndex === indexToRemove) clearForm();
    await saveSchedule(nextGames);
  };

  const launchGame = async (indexToLaunch) => {
    if (isSaving || !eventId) return;

    setIsSaving(true);
    setError('');

    try {
      const eventRef = doc(db, 'bingoMachineEvents', eventId);
      const snapshot = await getDoc(eventRef);
      if (!snapshot.exists()) throw new Error('Event not found.');

      const eventData = snapshot.data();
      const latestGames = (Array.isArray(eventData.gameSchedule)
        ? eventData.gameSchedule
        : createLegacySchedule(eventData)).map(normalizeGame);
      const gameToLaunch = latestGames[indexToLaunch];

      if (!gameToLaunch || !['scheduled', 'paused'].includes(gameToLaunch.status)) {
        throw new Error('This game is no longer available to launch.');
      }

      const launchedAt = Timestamp.now();
      const nextGames = latestGames.map((game, index) => {
        if (index === indexToLaunch) {
          return {
            ...game,
            status: 'active',
            completed: false,
            startedAt: game.startedAt || launchedAt,
            completedAt: null,
          };
        }

        if (game.status === 'active') {
          return {
            ...game,
            status: 'paused',
            generatedNumbers: Array.isArray(eventData.generatedNumbers)
              ? eventData.generatedNumbers
              : game.generatedNumbers,
            currentBall: eventData.currentBall || game.currentBall || null,
            nextBall: eventData.nextBall?.number ? eventData.nextBall : game.nextBall || null,
            pattern: eventData.selectedPattern || game.pattern,
            prizeAmount: eventData.prizeAmount || game.prizeAmount,
            customPattern: eventData.customPattern || game.customPattern || null,
          };
        }

        return game;
      });

      await setDoc(
        eventRef,
        {
          gameSchedule: nextGames,
          activeGameIndex: indexToLaunch,
          gameNumber: gameToLaunch.gameNumber || indexToLaunch + 1,
          selectedPattern: gameToLaunch.pattern || null,
          prizeAmount: gameToLaunch.prizeAmount || '',
          generatedNumbers: gameToLaunch.generatedNumbers,
          currentBall: gameToLaunch.currentBall,
          nextBall: gameToLaunch.nextBall || null,
          customPattern: gameToLaunch.customPattern || null,
          showWinner: false,
          winnerCalled: false,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      if (gameToLaunch.status === 'scheduled') {
        appendAuditEvent({
          eventId,
          game: gameToLaunch,
          type: 'GAME_STARTED',
          payload: {
            schedulePosition: indexToLaunch + 1,
          },
          idempotencyKey: `game-started-${gameToLaunch.id || indexToLaunch + 1}`,
        });
      }

      const machineUrl = `/bingo-machine?eventId=${encodeURIComponent(eventId)}`;
      if (window.opener && !window.opener.closed) {
        window.opener.location.assign(machineUrl);
        window.close();
        return;
      }

      navigate(machineUrl);
    } catch (launchError) {
      setError(launchError.message || 'Unable to launch this game.');
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (window.opener) {
      window.close();
      return;
    }
    navigate('/bingo-machine');
  };

  const saveAsTemplate = async () => {
    const user = getAuth().currentUser;
    if (!user?.email || games.length === 0 || isSaving) return;
    const templateName = window.prompt('Name this saved program:', `${eventName || 'Bingo'} Program`);
    if (!templateName?.trim()) return;
    setIsSaving(true);
    setTemplateMessage('');
    try {
      await addDoc(collection(db, 'programTemplates'), {
        name: templateName.trim(),
        description: `Saved from ${eventName || 'an existing event'}`,
        schedule: sanitizeProgramSchedule(games),
        createdBy: user.email,
        createdByUid: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setTemplateMessage('Program template saved.');
    } catch {
      setTemplateMessage('Unable to save this program as a template.');
    } finally { setIsSaving(false); }
  };

  if (isLoading) {
    return <main className="schedule-page"><div className="schedule-page-status">Loading schedule…</div></main>;
  }

  return (
    <main className="schedule-page">
      <div className="schedule-page-shell">
        <header className="schedule-page-header">
          <div>
            <span className="machine-eyebrow">EVENT SCHEDULE</span>
            <h1>{eventName || 'Game Schedule'}</h1>
            <p>Manage upcoming games without changing completed game history.</p>
          </div>
          <div className="schedule-header-actions">
            <button type="button" className="schedule-back-button" onClick={saveAsTemplate} disabled={games.length === 0 || isSaving}>Save Program as Template</button>
            <button type="button" className="schedule-back-button" onClick={() => navigate('/program-templates')}>Saved Programs</button>
            <button type="button" className="schedule-back-button" onClick={handleBack}>Back to Bingo Machine</button>
          </div>
        </header>

        {error && <p className="schedule-page-error">{error}</p>}
        {templateMessage && <p className="schedule-page-message">{templateMessage}</p>}

        <section className="schedule-builder">
          <div className="schedule-builder-header">
            <div>
              <span className="machine-eyebrow">
                {editingIndex === null ? 'ADD GAME' : 'EDIT UPCOMING GAME'}
              </span>
              <h2>{editingIndex === null ? 'Build the Event Schedule' : `Edit Game ${editingIndex + 1}`}</h2>
            </div>
            <span className="schedule-count">{games.length} {games.length === 1 ? 'game' : 'games'}</span>
          </div>

          <div className="schedule-builder-form">
            <input type="text" value={gameName} onChange={(event) => setGameName(event.target.value)}
              placeholder="Game name (optional)" className="schedule-input" />
            <select value={pattern} onChange={(event) => setPattern(event.target.value)} className="schedule-input">
              <option value="">Pattern</option>
              {bingoPatterns.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
            <div className="schedule-prize-input">
              <span>$</span>
              <input type="number" min="0" step="0.01" value={prize}
                onChange={(event) => setPrize(event.target.value)} placeholder="Prize" />
            </div>
            <button type="button" className="schedule-add-button" onClick={handleSubmit}
              disabled={!pattern || !prize || isSaving}>
              {isSaving ? 'Saving…' : editingIndex === null ? '+ Add Game' : 'Save Changes'}
            </button>
          </div>

          {pattern === 'custom' && (
            <div className="schedule-custom-pattern">
              <span className="machine-eyebrow">CUSTOM PATTERN</span>
              <p>Select the squares required to win. The center remains the free space.</p>
              <div className="schedule-custom-grid">
                {customPattern.map((row, rowIndex) => row.map((cell, columnIndex) => {
                  const isCenter = rowIndex === 2 && columnIndex === 2;
                  return (
                    <button
                      key={`${rowIndex}-${columnIndex}`}
                      type="button"
                      className={`schedule-custom-cell ${cell || isCenter ? 'selected' : ''} ${isCenter ? 'free' : ''}`}
                      onClick={() => !isCenter && toggleCustomCell(rowIndex, columnIndex)}
                      disabled={isCenter}
                      aria-label={isCenter ? 'Free space' : `Row ${rowIndex + 1}, column ${columnIndex + 1}`}
                      aria-pressed={cell || isCenter}
                    >
                      {isCenter ? 'FREE' : cell ? 'X' : ''}
                    </button>
                  );
                }))}
              </div>
            </div>
          )}

          {editingIndex !== null && (
            <button type="button" className="schedule-cancel-edit" onClick={clearForm}>Cancel editing</button>
          )}

          {games.length === 0 && (
            <div className="schedule-empty-state">
              <strong>No games scheduled</strong>
              <span>Add a game above to build this event&apos;s schedule.</span>
            </div>
          )}

          {games.length > 0 && (
            <div className="schedule-list">
              {games.map((game, index) => {
                const status = game.status || 'scheduled';
                const isCurrent = status === 'active';
                const isCompleted = status === 'completed';
                const isLocked = isCurrent || isCompleted;
                const canLaunch = status === 'scheduled' || status === 'paused';

                return (
                  <div key={game.id || `game-${index}`}
                    className={`schedule-game-row status-${status} ${isCurrent ? 'active' : ''}`}>
                    <div className="schedule-game-number">{game.gameNumber || index + 1}</div>
                    <div className="schedule-game-info">
                      <strong>{game.name || `Game ${index + 1}`}</strong>
                      <span>{getPatternLabel(game.pattern)}{' • '}${game.prizeAmount}</span>
                      <span className={`schedule-active-label status-${status}`}>
                        {status.toUpperCase()}
                      </span>
                    </div>
                    <div className="schedule-row-actions">
                      <button type="button" className="schedule-edit-button" onClick={() => beginEditing(index)}
                        disabled={isLocked || isSaving}>{isLocked ? 'Locked' : 'Edit'}</button>
                      <button type="button" className="schedule-remove-button" onClick={() => removeGame(index)}
                        disabled={isLocked || isSaving}>Remove</button>
                      {canLaunch && (
                        <button type="button" className="schedule-launch-button"
                          onClick={() => launchGame(index)} disabled={isSaving}>
                          {status === 'paused' ? 'Resume Game' : 'Launch Game'}
                        </button>
                      )}
                      {isCurrent && <span className="schedule-current-action">ACTIVE</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

export default BingoSchedule;
